// ============================================================================
//  ClaimProcedures — the Procedures table inside the inline claim editor
// ----------------------------------------------------------------------------
//  Add adds a row. The row is filled in and saved on its own, then Add again for
//  the next one — the same rhythm as the hospital claims panel, and the reason
//  each row carries its own Save rather than one Save for the table.
//
//  The columns are the ones that carry information:
//
//    G-DRG           picked from procedure_tarf. Choosing it fills the NAME —
//                    they are one choice, not two fields to keep in step.
//    Procedure Name  follows the G-DRG, so it is shown but not typed.
//    ICD10           what the procedure was done for. DERIVED, not typed and not
//                    chosen: procedure_tarf.icd (migration 042) lists the codes
//                    each procedure is indicated for, and the one that applies is
//                    the reason this patient was treated — which the claim already
//                    records as its diagnoses. Read-only, with no dropdown.
//    Date            constrained to the visit. An OPD attendance happened on one
//                    day, so that is the only option; an admission offers every
//                    day of the stay, because a procedure on the second of three
//                    days has to be recordable on that day.
//
//  Description and Status are deliberately absent: the description IS the name,
//  and the status was never anything a claims officer set.
//
//  Saving a procedure can change what the claim is BILLED on — the principal is
//  the highest-tariff procedure before anything else — so every write returns the
//  whole re-read claim and the parent adopts it. That is why onSaved takes the
//  claim rather than the row.
// ============================================================================
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { cachedGet } from './catalogueCache';
//  Pencil on Edit — see the note in ClaimDiagnoses.js.
import { Save, Trash2, X, Loader2, Star, Pencil } from 'lucide-react';
import {
  claimStoreProcedureTariff, claimStoreProcedureDates, claimStoreProcedure,
} from '../BaseURL';
import SearchSelect from './SearchSelect';
import VisitDateSelect from './VisitDateSelect';
import { confirmRemove } from './claimsDialog';

const BRAND = {
  green: '#1A9B7F', greenDark: '#137a63', greenSoft: '#e8f5f1',
  line: '#e2e8f0', ink: '#0f172a', inkSoft: '#475569', inkFaint: '#94a3b8',
  head: '#f1f5f9', danger: '#b91c1c', dangerSoft: '#fef2f2',
};

const th = {
  textAlign: 'left', padding: '9px 10px', fontSize: 10.5, fontWeight: 800,
  letterSpacing: 0.4, textTransform: 'uppercase', color: BRAND.inkSoft,
  background: BRAND.head, borderBottom: `1px solid ${BRAND.line}`, whiteSpace: 'nowrap',
};
const td = {
  padding: '8px 10px', fontSize: 12, color: BRAND.ink,
  borderBottom: `1px solid ${BRAND.line}`, verticalAlign: 'middle',
};
const cell = {
  width: '100%', boxSizing: 'border-box', padding: '6px 8px', fontSize: 12,
  border: `1px solid ${BRAND.green}`, borderRadius: 7, background: '#fff',
  color: BRAND.ink, outline: 'none', fontFamily: 'inherit',
};

const iconBtn = (tone) => ({
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: 28, height: 28, borderRadius: 7, cursor: 'pointer',
  border: `1px solid ${tone === 'danger' ? '#fecaca' : BRAND.line}`,
  background: tone === 'danger' ? BRAND.dangerSoft : '#fff',
  color: tone === 'danger' ? BRAND.danger : BRAND.greenDark,
});

//  `dmy` was here in triplicate across this file and its two sibling tables. Every
//  call site was a date cell, and those are VisitDateSelect now, which formats its
//  own value.

//  A row being edited. `id` is null for one that has not been saved yet, which is
//  also what decides POST vs PUT.
const blankRow = (firstDate) => ({
  id: null, gdrg_code: '', description: '', icd10_code: '',
  performed_at: firstDate || '', tariff: null,
  // Not sent to the server: where the derived ICD-10 came from, so the row can
  // say so.
  icdFrom: 'none', icdCount: 0,
});

export default function ClaimProcedures({
  claimId, source, scheme, age, rows = [], onSaved, readOnly,
  // The claim's own diagnoses. Used to derive the ICD-10: a procedure indicated
  // for 34 codes gets the one this patient was actually diagnosed with.
  diagnoses = [],
  // Raised by the fold's Add button, cleared by onAddConsumed once acted on.
  addSignal = false, onAddConsumed,
}) {
  const [catalogue, setCatalogue] = useState([]);
  //  What the catalogue could NOT offer, and why. The other three panels on this
  //  screen already show this; procedures did not read it at all, so a catalogue
  //  that had silently held rows back would look like a complete one. Nothing is
  //  held back today — the point is that it will say so if an import changes.
  const [catNote, setCatNote] = useState(null);
  const [dates, setDates] = useState([]);
  const [dateNote, setDateNote] = useState(null);
  const [drafts, setDrafts] = useState([]);       // unsaved + in-edit rows
  const [busy, setBusy] = useState(null);         // key of the row being written
  const [error, setError] = useState(null);

  //  The catalogue is priced per scheme and split by age, so both narrow it —
  //  the same two filters the service dropdown uses.
  useEffect(() => {
    let alive = true;
    cachedGet(claimStoreProcedureTariff, { scheme, age })
      .then((data) => {
        if (!alive) return;
        setCatalogue(data?.options || []);
        setCatNote(data?.note || null);
      })
      .catch(() => {
        if (!alive) return;
        setCatalogue([]);
        setCatNote('The procedure list could not be loaded.');
      });
    return () => { alive = false; };
  }, [scheme, age]);

  //  Which days this claim's procedures may be dated to. Re-fetched when the
  //  claim changes, because extending the stay adds days to this list.
  const loadDates = useCallback(() => {
    if (!claimId) return;
    //  ttl: 0 — deduped against the other two tables asking in the same commit,
    //  never served from store. A save can extend the stay, and this is re-run
    //  after one; a stored answer would be the list from before it.
    cachedGet(claimStoreProcedureDates, { id: claimId, source }, { ttl: 0 })
      .then((data) => { setDates(data?.dates || []); setDateNote(data?.note || null); })
      .catch(() => { setDates([]); setDateNote(null); });
  }, [claimId, source]);
  useEffect(loadDates, [loadDates]);

  const addRow = useCallback(() => {
    setError(null);
    setDrafts((d) => [...d, {
      // Unique for the life of this table: the row count can repeat once a draft
      // is saved and another added, and a repeated key makes React reuse the
      // wrong row's state.
      key: `new-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      ...blankRow(dates[0]),
    }]);
  }, [dates]);

  //  The fold's Add button, consumed as a one-shot flag rather than compared
  //  against a previous value. The fold mounts this component on the SAME click
  //  that raises the flag, so there is no previous value to compare with — a
  //  counter would be swallowed on exactly the first click, which is the one
  //  that matters. Clearing it on consumption also makes repeat clicks work.
  useEffect(() => {
    if (!addSignal) return;
    if (!readOnly) addRow();
    if (onAddConsumed) onAddConsumed();
  }, [addSignal, addRow, readOnly, onAddConsumed]);

  const editRow = (r) => {
    setError(null);
    setDrafts((d) => (d.some((x) => x.id === r.id) ? d : [...d, { key: `row-${r.id}`, ...r }]));
  };

  //  Promote a row to principal without opening it for editing — one click, the
  //  same shape as the Diagnoses table's star. The whole row is sent because the
  //  endpoint is an upsert, not a patch; sending only the flag would blank the
  //  columns it does not carry.
  const makePrincipal = async (r) => {
    setBusy(`row-${r.id}`); setError(null);
    try {
      const { data } = await axios.put(claimStoreProcedure, {
        id: r.id,
        gdrg_code: r.gdrg_code, description: r.description,
        icd10_code: r.icd10_code || null,
        performed_at: r.performed_at,
        tariff: r.tariff,
        is_principal: true,
      }, { params: { id: claimId, source } });
      //  The response is the RE-READ claim, so the Service at the top of the
      //  editor follows the new principal in the same round trip.
      if (data && onSaved) onSaved(data);
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not set the principal.');
    } finally { setBusy(null); }
  };

  //  Is any row explicitly flagged? When none is, the first row — the dearest,
  //  because the list is ordered by tariff — is the principal by default, and the
  //  star shows that rather than leaving the column blank on every row.
  const anyPrincipal = rows.some((r) => r.is_principal);

  const patch = (key, k, v) => setDrafts((d) => d.map((x) => (x.key === key ? { ...x, [k]: v } : x)));

  //  Picking the G-DRG sets the name, the tariff AND the ICD-10 with it. One
  //  choice.
  //
  //  The ICD-10 comes from procedure_tarf.icd (migration 042), which the NHIS
  //  procedure sheet fills: the sheet lists each procedure against the codes it is
  //  indicated for, so the catalogue knows them. Before that column existed this
  //  field was free text with nothing behind it, which is why it stayed empty.
  //
  //  The code is DERIVED, never picked. A procedure is indicated for several
  //  ICD-10s — ORTH17 for 34 of them — so "the first" would be a guess reading as
  //  a fact. But the one that applies is the reason THIS patient was treated, and
  //  the claim already records that: its own diagnoses. So the indication that
  //  matches one of the claim's diagnoses wins, and only failing that does the
  //  first stand in.
  //
  //  Returns the code and WHY, because "matched to this claim's diagnosis" and
  //  "first of 34, unverified" are different facts and the officer should be able
  //  to tell them apart without opening anything.
  const indicationFor = (inds, claimDiagnoses) => {
    if (!inds.length) return { code: '', from: 'none', count: 0 };
    const onClaim = new Set((claimDiagnoses || [])
      .map((d) => String(d.icd10_code || '').toUpperCase()).filter(Boolean));
    const hit = inds.find((i) => onClaim.has(String(i.icd10_code).toUpperCase()));
    if (hit) return { code: hit.icd10_code, from: 'diagnosis', count: inds.length };
    return {
      code: inds[0].icd10_code,
      from: inds.length > 1 ? 'first-of-many' : 'only',
      count: inds.length,
    };
  };

  //  Already on the claim? Said at the moment of picking, not at save. The server
  //  refuses it either way, but finding out three fields later is a worse way to
  //  learn it. Same rule as the server: the code AND the day, because the same
  //  procedure on another day of a stay is a second procedure.
  //  A ROW IS NOT A DUPLICATE OF ITSELF. See the note in ClaimDiagnoses.js: an
  //  edited row was compared against every saved row including its own, so
  //  substituting a procedure - or correcting only its date - was refused with
  //  "already on this claim", naming the row being changed.
  const duplicateOf = (key, code, when, selfId) => {
    const same = (a, b) => String(a || '') === String(b || '');
    const isSelf = (r) => selfId != null && String(r.id) === String(selfId);
    const inSaved = rows.find((r) => !isSelf(r) && same(r.gdrg_code, code)
      && same(r.performed_at, when));
    if (inSaved) return 'saved';
    const inDraft = drafts.find((x) => x.key !== key && same(x.gdrg_code, code)
      && same(x.performed_at, when));
    return inDraft ? 'draft' : null;
  };

  const pickProcedure = (key, code) => {
    const opt = catalogue.find((o) => o.code === code);
    const chosen = indicationFor(opt?.indications || [], diagnoses);
    const row = drafts.find((x) => x.key === key);
    const dup = duplicateOf(key, code, row?.performed_at || dates[0], row?.id);
    setError(dup
      ? `${opt?.name || code} is already on this claim`
        + `${dup === 'draft' ? ' — in a row above, not yet saved' : ''}.`
      : null);
    setDrafts((d) => d.map((x) => {
      if (x.key !== key) return x;
      return {
        ...x,
        gdrg_code: code,
        description: opt?.name || '',
        tariff: opt?.tariff ?? null,
        //  Always re-derived. A code left from the previously-picked procedure is
        //  not an indication for this one.
        icd10_code: chosen.code,
        icdFrom: chosen.from,
        icdCount: chosen.count,
        duplicate: Boolean(dup),
      };
    }));
  };

  //  The claim's own procedure, offered when the catalogue does not contain its
  //  G-DRG — a code the tariff import has since dropped, or a scheme whose
  //  catalogue has changed. Without it, editing a saved row opens with an empty
  //  picker and the officer has to find the procedure again to change a date.
  //  The same fault the medications table had, where matching by name found only
  //  4 of 47 drugs.
  const optionsWith = (row) => {
    if (!row.gdrg_code) return catalogue;
    if (catalogue.some((o) => o.code === row.gdrg_code)) return catalogue;
    return [{
      code: row.gdrg_code, name: row.description || row.gdrg_code,
      tariff: row.tariff ?? null, category: null, specialty: null,
      indications: [], onThisClaim: true,
    }, ...catalogue];
  };

  const drop = (key) => setDrafts((d) => d.filter((x) => x.key !== key));

  const save = async (row) => {
    if (!row.gdrg_code) { setError('Pick a G-DRG first.'); return; }
    //  Re-checked here as well as on pick: the DATE can change after the procedure
    //  was chosen, and moving a row onto a day that already has it is the same
    //  duplicate arriving by a different route.
    const dup = duplicateOf(row.key, row.gdrg_code, row.performed_at || dates[0], row.id);
    if (dup) {
      setError(`${row.description || row.gdrg_code} is already on this claim`
        + `${dup === 'draft' ? ' — in a row above, not yet saved' : ''}.`);
      return;
    }
    setBusy(row.key); setError(null);
    try {
      const body = {
        id: row.id || undefined,
        gdrg_code: row.gdrg_code, description: row.description,
        icd10_code: row.icd10_code || null,
        performed_at: row.performed_at || dates[0] || null,
        tariff: row.tariff,
      };
      const { data } = await axios[row.id ? 'put' : 'post'](
        claimStoreProcedure, body, { params: { id: claimId, source } });
      drop(row.key);
      // The write returns the RE-READ claim: adding a procedure can change the
      // principal, and with it the service code at the top of the panel.
      if (data && onSaved) onSaved(data);
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not save the procedure.');
    } finally { setBusy(null); }
  };

  const remove = async (r) => {
    // eslint-disable-next-line no-alert
    if (!(await confirmRemove({ what: 'procedure', name: r.description || r.gdrg_code }))) return;
    setBusy(`row-${r.id}`); setError(null);
    try {
      const { data } = await axios.delete(claimStoreProcedure,
        { params: { id: claimId, source, rowId: r.id } });
      if (data && onSaved) onSaved(data);
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not remove the procedure.');
    } finally { setBusy(null); }
  };

  //  One draft row. A FUNCTION rather than inline JSX because it renders in
  //  two places: a new row at the top of the table, an edited row in the
  //  position of the row it replaces. Clicking Edit must not move anything.
  const draftTr = (row, i) => (
                <tr key={row.key} style={{
                  background: row.duplicate ? BRAND.dangerSoft : BRAND.greenSoft }}>
                  <td style={{ ...td, color: BRAND.inkFaint }}>{'\u2014'}</td>
                  {/*  Typed into, not scrolled through. 296 procedures for one
                       scheme is far past what a native <select> is usable for. */}
                  <td style={td}>
                    <SearchSelect
                      options={optionsWith(row)}
                      value={row.gdrg_code}
                      onChange={(code) => pickProcedure(row.key, code)}
                      placeholder="Select G-DRG"
                      searchPlaceholder="Type a name or code…"
                      //  A row ADDED opens its list with the cursor already in the
                      //  search box. Not a saved row opened for editing — see the
                      //  note in ClaimDiagnoses.
                      autoOpen={!row.id}
                      ariaLabel="G-DRG"
                      //  The closed field shows the CODE alone. It sits in the
                      //  G-DRG column and the Procedure Name column beside it
                      //  already carries the name — the same duplication the
                      //  diagnoses table had, where the code column read back a
                      //  name. The LIST still shows both, because that is where
                      //  you choose.
                      triggerOf={(o) => (
                        <strong style={{ fontWeight: 800 }}>{o.code}</strong>
                      )}
                      //  The G-DRG column holds the G-DRG. The name has a column of
                      //  its own immediately to the right, so "OBGY03 — Operative
                      //  Laparoscopy" here printed it twice and left the code — the
                      //  thing this column exists for — squeezed and truncated.
                      triggerLabelOf={(o) => (
                        <strong style={{ fontWeight: 800 }}>{o.code}</strong>
                      )}
                      //  ── SHOWN, BUT NOT THIS PATIENT'S BAND ────────────────
                      //  Every registered procedure is offered now; they used to
                      //  be dropped, so one just added to the setup screen was
                      //  absent with nothing saying why. PSUR39 and PSUR39C are
                      //  the same operation filed for the two bands, and the one
                      //  that does not match sorts last carrying this label — so
                      //  a paediatric tariff cannot land on an adult unseen.
                      primaryOf={(o) => (
                        <span>
                          {o.name}
                          {o.out_of_band && (
                            <span
                              title={'This is the tariff for the other age band. It is '
                                + 'shown so you can use it if the date of birth is the '
                                + 'thing that is wrong — but NHIA prices on the band.'}
                              style={{
                                marginLeft: 6, fontSize: 9.5, fontWeight: 800,
                                padding: '1px 5px', borderRadius: 4,
                                background: '#FFF8EC', color: '#8A5510',
                                border: '1px solid rgba(224,139,47,.35)',
                              }}>OTHER AGE BAND</span>
                          )}
                        </span>
                      )}
                      // The specialty and the tariff are what tell two
                      // similarly-named procedures apart.
                      secondaryOf={(o) => [
                        o.category,
                        o.tariff != null ? `GHS ${Number(o.tariff).toFixed(2)}` : null,
                      ].filter(Boolean).join('  ·  ')} />
                  </td>
                  {/*  Follows the G-DRG. Shown so the row reads as a sentence, not
                       typed, because the two must not be able to disagree. */}
                  <td style={{ ...td, color: BRAND.inkSoft }}>
                    {row.description || <span style={{ color: BRAND.inkFaint }}>
                      Pick a G-DRG
                    </span>}
                  </td>
                  {/*  Read-only. The code comes WITH the procedure — derived from
                       the catalogue and from the claim's own diagnoses — so there is
                       nothing to choose here and no dropdown to open. The hint says
                       where it came from, because "matches a diagnosis on this claim"
                       and "1 of 34 indications" are different facts. */}
                  <td style={td}>
                    {row.icd10_code ? (
                      <div>
                        <span style={{ fontWeight: 700 }}>{row.icd10_code}</span>
                        {row.icdFrom === 'diagnosis' && (
                          <div style={{ fontSize: 9.5, color: BRAND.greenDark, marginTop: 1 }}>
                            matches a diagnosis on this claim
                          </div>
                        )}
                        {row.icdFrom === 'first-of-many' && (
                          <div style={{ fontSize: 9.5, color: BRAND.inkFaint, marginTop: 1 }}>
                            1 of {row.icdCount} indications
                          </div>
                        )}
                      </div>
                    ) : (
                      <span style={{ color: BRAND.inkFaint }}>
                        {row.gdrg_code ? 'none in the catalogue' : '—'}
                      </span>
                    )}
                  </td>
                  <td style={td}>{dateCell(row)}</td>
                  {/*  The Principal column, on a draft row too — the header has
                       seven columns and a six-cell row would slide every cell
                       after it one place left. Not settable until the row is
                       saved: the flag is written against a row id, and a draft
                       has none yet. */}
                  <td style={td}>
                    <span title="Save the row first, then star it"
                      style={{ display: 'inline-block', lineHeight: 0, opacity: .35 }}>
                      <Star size={15} color={BRAND.line} fill="none" />
                    </span>
                  </td>
                  <td style={td}>
                    <div style={{ display: 'flex', gap: 5 }}>
                      <button type="button" title="Save" style={iconBtn()}
                        aria-label="Save procedure"
                        disabled={busy === row.key}
                        onClick={() => save(row)}>
                        {busy === row.key
                          ? <Loader2 size={13} className="spin" />
                          : <Save size={13} />}
                      </button>
                      <button type="button" title="Discard" style={iconBtn('danger')}
                        aria-label="Discard row"
                        onClick={() => drop(row.key)}><X size={13} /></button>
                    </div>
                  </td>
                </tr>
  );

  //  New rows only — an edited row is drawn where it already sits.
  const newDrafts = drafts.filter((d) => !d.id);
  const draftFor = (id) => drafts.find((d) => d.id != null && d.id === id);

  //  Saved rows in DATE order, ties broken by id so the order is stable.
  //
  //  This is the second half of putting new rows at the top. A draft is typed at
  //  the top of the table because that is where you can see it; on save it has to
  //  settle where it belongs, which for an admission spanning several days is its
  //  own day — not the top, and not the end. Dates are 'YYYY-MM-DD' strings, so a
  //  plain string compare IS date order.
  const saved = rows
    .slice()
    .sort((a, b) => String(a.performed_at || '').localeCompare(String(b.performed_at || ''))
      || (Number(a.id) || 0) - (Number(b.id) || 0));
  const isEmpty = saved.length === 0 && drafts.length === 0;

  const dateCell = (row) => (
    <VisitDateSelect
      value={row.performed_at || dates[0]}
      dates={dates}
      style={cell}
      onChange={(d) => patch(row.key, 'performed_at', d)}
    />
  );

  // ── Moving a SAVED procedure to another day of the stay ───────────────────
  //  The same control the draft row has, on the rows already saved — changing the
  //  day should not mean reopening the whole row.
  //
  //  The PUT carries every field because this endpoint replaces the row. The
  //  re-read claim comes back and is adopted, because moving a procedure can
  //  change which one is the PRINCIPAL and with it the service at the top.
  const changeSavedDate = async (r, when) => {
    if (!when || when === String(r.performed_at || '').slice(0, 10)) return;
    setBusy(`date-${r.id}`); setError(null);
    try {
      const { data } = await axios.put(claimStoreProcedure, {
        id: r.id,
        gdrg_code: r.gdrg_code,
        description: r.description,
        icd10_code: r.icd10_code || null,
        performed_at: when,
        tariff: r.tariff,
      }, { params: { id: claimId, source } });
      if (data && onSaved) onSaved(data);
    } catch (e) {
      setError(e?.response?.data?.message
        || 'Could not move that procedure to another day.');
    } finally { setBusy(null); }
  };

  return (
    <div>
      {/*  No Add button here — there is ONE, in the fold's header, where the
           design puts it. Two would be ambiguous about which one works. */}
      {dateNote && (
        <div style={{ fontSize: 10.5, color: BRAND.inkFaint, marginBottom: 8 }}>
          {dateNote}
        </div>
      )}

      {/*  Amber, and worded by the server, so a catalogue that cannot offer part
           of what was imported says so here rather than looking complete. Matches
           ClaimDiagnoses, ClaimInvestigations and ClaimMedications, which already
           show their own. */}
      {catNote && (
        <div style={{
          fontSize: 10.5, padding: '6px 9px', borderRadius: 6, marginBottom: 8,
          background: '#fffbeb', color: '#92400e', border: '1px solid #fde68a',
        }}>{catNote}</div>
      )}

      {error && (
        <div style={{
          marginBottom: 8, padding: '7px 10px', borderRadius: 7, fontSize: 11.5,
          background: BRAND.dangerSoft, color: BRAND.danger,
          border: '1px solid #fecaca',
        }}>{error}</div>
      )}

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', minWidth: 620, borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ ...th, width: 34 }}>#</th>
              <th style={{ ...th, width: 170 }}>G-DRG</th>
              <th style={th}>Procedure Name</th>
              <th style={{ ...th, width: 110 }}>ICD10</th>
              <th style={{ ...th, width: 130 }}>Date</th>
              {/*  A claim with two procedures is billed on ONE of them, and the
                   default is whichever costs more. That is a default, not an
                   answer — so it is chooseable, exactly as the principal
                   diagnosis is. */}
              <th style={{ ...th, width: 76 }}>Principal</th>
              <th style={{ ...th, width: 76 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {/*  A duplicate draft row turns red, so the message above names which
                 row it is talking about. */}
            {newDrafts.map(draftTr)}

            {isEmpty && (
              <tr><td colSpan={7} style={{
                ...td, textAlign: 'center', color: BRAND.inkFaint, padding: '22px 10px',
              }}>No procedures available</td></tr>
            )}

            {saved.map((r, i) => {
              //  Being edited? Draw the draft HERE, in this row's place.
              const d = draftFor(r.id);
              if (d) return draftTr(d, i);
              return (
              <tr key={r.id}>
                <td style={{ ...td, color: BRAND.inkFaint }}>{i + 1}</td>
                <td style={{ ...td, fontWeight: 700 }}>{r.gdrg_code || '—'}</td>
                <td style={td}>{r.description}</td>
                <td style={td}>{r.icd10_code || '—'}</td>
                <td style={td}>
                  <VisitDateSelect
                    value={r.performed_at}
                    dates={dates}
                    disabled={readOnly}
                    busy={busy === `date-${r.id}`}
                    ariaLabel={`Day for ${r.description || r.gdrg_code}`}
                    onChange={(d) => changeSavedDate(r, d)}
                  />
                </td>
                {/*  The star. Only ONE procedure carries it; clicking another
                     moves it, and the server clears the rest in the same write.
                     When nothing is flagged the dearest row is the principal by
                     default, and it is shown hollow-but-lit so the claim always
                     says what it is billed on rather than showing five blanks. */}
                <td style={td}>
                  {(() => {
                    const isDefault = !anyPrincipal && i === 0;
                    const on = !!r.is_principal;
                    return (
                      <button type="button"
                        title={on ? 'The principal procedure'
                          : isDefault
                            ? 'Billed on this by default — the highest tariff. Click to fix it in place.'
                            : 'Make this the principal procedure'}
                        aria-label={on
                          ? `${r.description} is the principal procedure`
                          : `Make ${r.description} the principal procedure`}
                        aria-pressed={on}
                        disabled={readOnly || busy === `row-${r.id}` || on}
                        onClick={() => !on && makePrincipal(r)}
                        style={{
                          border: 0, background: 'none', padding: 2, lineHeight: 0,
                          cursor: readOnly || on ? 'default' : 'pointer',
                        }}>
                        <Star size={15}
                          color={on ? BRAND.green : isDefault ? BRAND.green : BRAND.line}
                          fill={on ? BRAND.green : 'none'} />
                      </button>
                    );
                  })()}
                </td>
                <td style={td}>
                  {!readOnly && (
                    <div style={{ display: 'flex', gap: 5 }}>
                      <button type="button"
                        title="Change this procedure — pick another and save"
                        style={iconBtn()}
                        aria-label={`Edit ${r.description || r.gdrg_code}`}
                        onClick={() => editRow(r)}><Pencil size={13} /></button>
                      <button type="button" title="Remove" style={iconBtn('danger')}
                        aria-label={`Remove ${r.description || r.gdrg_code}`}
                        onClick={() => remove(r)}><Trash2 size={13} /></button>
                    </div>
                  )}
                </td>
              </tr>
              );
            })}

          </tbody>
        </table>
      </div>
    </div>
  );
}
